import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { announcementsAPI, dashboardAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Megaphone, Plus, Pencil, Trash2, ExternalLink, Clock, Link2 } from 'lucide-react';

const EMPTY_AUDIENCE = {
  all: true, roles: [], companies: [], locations: [], clients: [], supervisory_groups: [],
};

const toISO = (localValue) => (localValue ? new Date(localValue).toISOString() : null);
const toLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CheckList = ({ title, options, selected, onToggle, searchable, testId }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-fg-muted">{title}</Label>
      {searchable && (
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}`}
          className="h-8 mt-1.5" data-testid={`${testId}-search`} />
      )}
      <div className="mt-1.5 max-h-40 overflow-y-auto scrollbar-thin rounded-md border border-line p-2 space-y-1.5"
        data-testid={testId}>
        {shown.length === 0 && <p className="text-xs text-fg-muted">Nothing to choose from.</p>}
        {shown.slice(0, 200).map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => onToggle(o.value)}
              data-testid={`${testId}-${o.value}`} />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const AnnouncementDialog = ({ open, onOpenChange, initial, options, onSaved }) => {
  const [form, setForm] = useState({
    title: '', body: '', audience: EMPTY_AUDIENCE, publish_at: '', expires_at: '',
    is_active: true, link_url: '', link_label: '', priority: 0, acknowledgement_required: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      title: initial.title || '', body: initial.body || '',
      audience: { ...EMPTY_AUDIENCE, ...(initial.audience || {}) },
      publish_at: toLocal(initial.publish_at), expires_at: toLocal(initial.expires_at),
      is_active: initial.is_active !== false, link_url: initial.link_url || '',
      link_label: initial.link_label || '', priority: initial.priority || 0,
      acknowledgement_required: !!initial.acknowledgement_required,
    } : {
      title: '', body: '', audience: EMPTY_AUDIENCE, publish_at: '', expires_at: '',
      is_active: true, link_url: '', link_label: '', priority: 0, acknowledgement_required: false,
    });
  }, [open, initial]);

  const setAud = (patch) => setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));
  const toggle = (field) => (value) => setAud({
    [field]: form.audience[field].includes(value)
      ? form.audience[field].filter((v) => v !== value)
      : [...form.audience[field], value],
  });

  const save = async () => {
    if (form.title.trim().length < 2) { toast.error('Give the announcement a title'); return; }
    const a = form.audience;
    if (!a.all && !a.roles.length && !a.companies.length && !a.locations.length
        && !a.clients.length && !a.supervisory_groups.length) {
      toast.error('Choose who this is for, or tick "Everyone"');
      return;
    }
    setSaving(true);
    const body = {
      ...form,
      publish_at: toISO(form.publish_at),
      expires_at: toISO(form.expires_at),
      attachment_ids: initial?.attachment_ids || [],
      link_url: form.link_url || null,
      link_label: form.link_label || null,
    };
    try {
      if (initial?.id) await announcementsAPI.update(initial.id, body);
      else await announcementsAPI.create(body);
      toast.success(initial?.id ? 'Announcement updated' : 'Announcement posted');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the announcement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="announcement-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit announcement' : 'New announcement'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="ann-title">Title</Label>
            <Input id="ann-title" value={form.title} maxLength={160}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="announcement-title" />
          </div>
          <div>
            <Label htmlFor="ann-body">Body</Label>
            <Textarea id="ann-body" rows={6} value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Markdown is supported — **bold**, lists, links."
              data-testid="announcement-body" />
          </div>

          <div className="rounded-md border border-line p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <Checkbox checked={form.audience.all}
                onCheckedChange={(v) => setAud({ all: !!v })}
                data-testid="announcement-audience-all" />
              Everyone at TriCorps
            </label>
            {!form.audience.all && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CheckList title="Roles" testId="audience-roles"
                  options={(options.roles || []).map((r) => ({ value: r.key, label: r.label }))}
                  selected={form.audience.roles} onToggle={toggle('roles')} />
                <CheckList title="Companies" testId="audience-companies"
                  options={(options.companies || []).map((c) => ({ value: c, label: c }))}
                  selected={form.audience.companies} onToggle={toggle('companies')} />
                <CheckList title="Locations" testId="audience-locations"
                  options={(options.locations || []).map((l) => ({ value: l.id, label: l.name }))}
                  selected={form.audience.locations} onToggle={toggle('locations')} searchable />
                <CheckList title="Client accounts" testId="audience-clients"
                  options={(options.clients || []).map((c) => ({ value: c.id, label: c.name }))}
                  selected={form.audience.clients} onToggle={toggle('clients')} searchable />
                <CheckList title="Supervisory groups" testId="audience-groups"
                  options={(options.supervisory_groups || []).map((g) => ({ value: g.id, label: g.name }))}
                  selected={form.audience.supervisory_groups}
                  onToggle={toggle('supervisory_groups')} searchable />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ann-publish">Publish at (optional)</Label>
              <Input id="ann-publish" type="datetime-local" value={form.publish_at}
                onChange={(e) => setForm((f) => ({ ...f, publish_at: e.target.value }))}
                data-testid="announcement-publish-at" />
              <p className="text-xs text-fg-muted mt-1">Leave blank to publish immediately.</p>
            </div>
            <div>
              <Label htmlFor="ann-expires">Expires at (optional)</Label>
              <Input id="ann-expires" type="datetime-local" value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                data-testid="announcement-expires-at" />
              <p className="text-xs text-fg-muted mt-1">Leave blank and it never expires.</p>
            </div>
            <div>
              <Label htmlFor="ann-link">Call-to-action link (optional)</Label>
              <Input id="ann-link" value={form.link_url} placeholder="https://…"
                onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
                data-testid="announcement-link-url" />
            </div>
            <div>
              <Label htmlFor="ann-link-label">Link label</Label>
              <Input id="ann-link-label" value={form.link_label} placeholder="Read the policy"
                onChange={(e) => setForm((f) => ({ ...f, link_label: e.target.value }))}
                data-testid="announcement-link-label" />
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm">
            <Switch checked={form.is_active}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              data-testid="announcement-active" />
            Active
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="announcement-save">
            {initial?.id ? 'Save changes' : 'Post announcement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LinkDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState({ label: '', description: '', url: '', icon: 'link', sort_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      label: initial.label || '', description: initial.description || '', url: initial.url || '',
      icon: initial.icon || 'link', sort_order: initial.sort_order || 0,
      is_active: initial.is_active !== false,
    } : { label: '', description: '', url: '', icon: 'link', sort_order: 0, is_active: true });
  }, [open, initial]);

  const save = async () => {
    if (!form.label.trim()) { toast.error('Give the link a label'); return; }
    setSaving(true);
    const body = { ...form, audience: EMPTY_AUDIENCE };
    try {
      if (initial?.id) await dashboardAPI.updateLink(initial.id, body);
      else await dashboardAPI.createLink(body);
      toast.success('Saved');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="link-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? 'Edit link' : 'New company link'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="lnk-label">Label</Label>
            <Input id="lnk-label" value={form.label} data-testid="link-label"
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} /></div>
          <div><Label htmlFor="lnk-desc">Description</Label>
            <Input id="lnk-desc" value={form.description} data-testid="link-description"
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          <div><Label htmlFor="lnk-url">URL</Label>
            <Input id="lnk-url" value={form.url} placeholder="https://…" data-testid="link-url"
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
            <p className="text-xs text-fg-muted mt-1">
              Use a path like <code>/help</code> for a page inside TriCorps360.
            </p></div>
          <div><Label htmlFor="lnk-icon">Icon</Label>
            <Input id="lnk-icon" value={form.icon} data-testid="link-icon"
              placeholder="globe · life-buoy · newspaper · graduation-cap · link"
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} /></div>
          <div><Label htmlFor="lnk-order">Sort order</Label>
            <Input id="lnk-order" type="number" value={form.sort_order} data-testid="link-sort"
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} /></div>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={form.is_active} data-testid="link-active"
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            Shown on dashboards
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="link-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Announcements() {
  const [mine, setMine] = useState([]);
  const [canAuthor, setCanAuthor] = useState(false);
  const [manageRows, setManageRows] = useState([]);
  const [options, setOptions] = useState({});
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkEditing, setLinkEditing] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('archive');

  const loadMine = async () => {
    const res = await announcementsAPI.list(100);
    setMine(res.data.announcements || []);
    setCanAuthor(!!res.data.can_author);
    return !!res.data.can_author;
  };

  const loadManage = async () => {
    try {
      const [rows, opts] = await Promise.all([
        announcementsAPI.manage(), announcementsAPI.audienceOptions(),
      ]);
      setManageRows(rows.data.announcements || []);
      setOptions(opts.data || {});
    } catch { /* not an author */ }
  };

  const loadLinks = async () => {
    try {
      const res = await dashboardAPI.listLinks(true);
      setLinks(res.data || []);
    } catch { setLinks(null); }
  };

  useEffect(() => {
    (async () => {
      try {
        const author = await loadMine();
        if (author) await Promise.all([loadManage(), loadLinks()]);
      } catch {
        toast.error('Could not load announcements');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'links' && links !== null) setTab('links');
  }, [searchParams, links]);

  const removeAnn = async (id) => {
    try {
      await announcementsAPI.remove(id);
      toast.success('Deleted');
      await Promise.all([loadMine(), loadManage()]);
    } catch { toast.error('Could not delete that announcement'); }
  };

  const removeLink = async (id) => {
    try {
      await dashboardAPI.deleteLink(id);
      toast.success('Deleted');
      await loadLinks();
    } catch { toast.error('Could not delete that link'); }
  };

  if (loading) {
    return <div className="space-y-4" data-testid="announcements-loading">
      <div className="h-9 w-52 skeleton-pulse rounded" />
      <div className="h-32 skeleton-pulse rounded-lg" />
    </div>;
  }

  const archive = (
    <div className="space-y-3" data-testid="announcements-archive">
      {mine.length === 0 && (
        <div className="stat-card text-center py-12">
          <Megaphone className="w-7 h-7 mx-auto text-fg-muted mb-2" />
          <p className="text-sm text-fg-muted">There are no announcements for you right now.</p>
        </div>
      )}
      {mine.map((a) => (
        <article key={a.id} className="stat-card" data-testid={`archive-item-${a.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-fg">{a.title}</h2>
              <p className="text-xs text-fg-muted mt-0.5">
                {a.author_name || 'TriCorps360'} · {formatDate(a.publish_at || a.created_at)}
              </p>
            </div>
            {!a.is_read && <Badge className="shrink-0">New</Badge>}
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none mt-3 text-fg">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body || ''}</ReactMarkdown>
          </div>
          {a.link_url && (
            <a href={a.link_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm mt-3 text-[hsl(var(--brand-accent))] hover:underline">
              {a.link_label || 'Open link'} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </article>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="announcements-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[hsl(var(--brand-accent))]" aria-hidden="true" />
            Announcements
          </h1>
          <p className="text-sm text-fg-muted mt-1">
            Notices aimed at your role, company, location, accounts or teams.
          </p>
        </div>
        {canAuthor && (
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="announcements-new">
            <Plus className="w-4 h-4 mr-1.5" />New announcement
          </Button>
        )}
      </div>

      {!canAuthor ? archive : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="archive" data-testid="tab-archive">For me</TabsTrigger>
            <TabsTrigger value="manage" data-testid="tab-manage">All announcements</TabsTrigger>
            {links !== null && (
              <TabsTrigger value="links" data-testid="tab-links">Company links</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="archive" className="mt-4">{archive}</TabsContent>

          <TabsContent value="manage" className="mt-4">
            <div className="space-y-2" data-testid="announcements-manage">
              {manageRows.length === 0 && (
                <p className="text-sm text-fg-muted py-6">Nothing posted yet.</p>
              )}
              {manageRows.map((a) => (
                <div key={a.id} className="stat-card flex items-start justify-between gap-3"
                  data-testid={`manage-item-${a.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-fg truncate">{a.title}</span>
                      {a.is_scheduled && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />Scheduled
                        </Badge>
                      )}
                      {a.is_expired && <Badge variant="outline" className="text-xs">Expired</Badge>}
                      {a.is_active === false && <Badge variant="outline" className="text-xs">Off</Badge>}
                    </div>
                    <p className="text-xs text-fg-muted mt-1">
                      {a.audience?.all ? 'Everyone' : [
                        a.audience?.roles?.length && `${a.audience.roles.length} role(s)`,
                        a.audience?.companies?.length && `${a.audience.companies.length} company`,
                        a.audience?.locations?.length && `${a.audience.locations.length} location(s)`,
                        a.audience?.clients?.length && `${a.audience.clients.length} account(s)`,
                        a.audience?.supervisory_groups?.length && `${a.audience.supervisory_groups.length} group(s)`,
                      ].filter(Boolean).join(' · ')}
                      {' · '}read by {a.reach_count || 0}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      aria-label={`Edit ${a.title}`} data-testid={`manage-edit-${a.id}`}
                      onClick={() => { setEditing(a); setDialogOpen(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600"
                      aria-label={`Delete ${a.title}`} data-testid={`manage-delete-${a.id}`}
                      onClick={() => removeAnn(a.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {links !== null && (
            <TabsContent value="links" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button size="sm" variant="outline"
                  onClick={() => { setLinkEditing(null); setLinkDialogOpen(true); }}
                  data-testid="links-new">
                  <Plus className="w-4 h-4 mr-1.5" />New link
                </Button>
              </div>
              <div className="space-y-2" data-testid="links-manage">
                {links.map((l) => (
                  <div key={l.id} className="stat-card flex items-center justify-between gap-3"
                    data-testid={`link-row-${l.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-fg-muted shrink-0" />
                        <span className="text-sm font-semibold text-fg truncate">{l.label}</span>
                        {!l.url && <Badge variant="outline" className="text-xs">Needs a URL</Badge>}
                        {l.is_active === false && <Badge variant="outline" className="text-xs">Hidden</Badge>}
                      </div>
                      <p className="text-xs text-fg-muted mt-1 truncate">{l.url || l.description}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        aria-label={`Edit ${l.label}`} data-testid={`link-edit-${l.id}`}
                        onClick={() => { setLinkEditing(l); setLinkDialogOpen(true); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600"
                        aria-label={`Delete ${l.label}`} data-testid={`link-delete-${l.id}`}
                        onClick={() => removeLink(l.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}

      <AnnouncementDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing}
        options={options} onSaved={async () => { await Promise.all([loadMine(), loadManage()]); }} />
      <LinkDialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen} initial={linkEditing}
        onSaved={loadLinks} />
    </div>
  );
}
